import axiosInstance from "../../utils/axiosConfig";
import React, { useEffect, useState } from "react";
import { BsArrowLeftSquare, BsArrowRightSquare } from "react-icons/bs";
import { TiArrowSortedUp, TiArrowSortedDown } from "react-icons/ti";
import { noTransaction } from "../../assets/images";
import { API_URL } from "../../constant";
import { useAuth } from "../../context/auth-context";
import { CiSearch } from "react-icons/ci";

// Helpers for new log shape: each log has kyc1, kyc2, kyc3
const getLogName = (log: any) => {
  const d = log?.kyc1?.data;
  if (!d) return "—";
  if (d.fullName) return d.fullName;
  return [d.firstName, d.lastName].filter(Boolean).join(" ") || "—";
};
const getLogEmail = (log: any) =>
  log?.user?.email?.address ?? log?.email ?? "—";
const isKyc2Success = (log: any) => {
  const s = String(log?.kyc2?.status ?? "").toLowerCase();
  return s === "success" || s === "approved" || s === "completed";
};

// Level 3 not started = no kyc3 or status empty/not-started
const isKyc3NotStarted = (log: any) => {
  const s = String(log?.kyc3?.status ?? "").toLowerCase();
  return !s || s === "not-started" || s === "notstarted";
};

// Level 2 tab: L2 pending OR (L2 completed AND L3 not-started)
const belongsToLevel2 = (log: any) =>
  !isKyc2Success(log) || isKyc3NotStarted(log);
// Level 3 tab: L2 completed AND (L3 pending OR L3 completed)
const belongsToLevel3 = (log: any) =>
  isKyc2Success(log) && !isKyc3NotStarted(log);

const normalizeStatus = (raw: string) => {
  const s = String(raw ?? "").toLowerCase();
  if (s === "success" || s === "approved" || s === "completed")
    return "completed/approved";
  if (s === "rejected") return "rejected";
  if (s === "pending") return "pending";
  return "not-started";
};

// Status for a row in Level 2 tab: L2 completed => "Completed", else kyc2 status
const getLogStatusForLevel2 = (log: any) => {
  if (isKyc2Success(log)) return "completed/approved";
  const raw = log?.kyc2?.status ?? log?.kyc2?.data?.status;
  return normalizeStatus(raw);
};

// Status for a row in Level 3 tab: kyc3 status
const getLogStatusForLevel3 = (log: any) => {
  const raw = log?.kyc3?.status ?? log?.kyc3?.data?.status;
  return normalizeStatus(raw);
};

// Row status depends on which tab we're in (level "all" = derive from log)
const getEffectiveLevel = (log: any): 2 | 3 => (belongsToLevel3(log) ? 3 : 2);
const getRowStatus = (log: any, viewMode: "all" | 2 | 3) => {
  const level = viewMode === "all" ? getEffectiveLevel(log) : viewMode;
  return level === 2 ? getLogStatusForLevel2(log) : getLogStatusForLevel3(log);
};

const getLogDate = (log: any, level?: "all" | 2 | 3) => {
  const effective =
    level === "all" || level == null ? getEffectiveLevel(log) : level;
  if (effective === 3) {
    return log?.kyc3?.data?.updatedAt ?? log?.kyc3?.data?.createdAt ?? "";
  }
  return log?.kyc2?.data?.updatedAt ?? log?.kyc2?.data?.createdAt ?? "";
};

const CustomerLogs = ({
  setOpenCustomerLog,
  refetch,
  setCustomerDeet,
}: any) => {
  const { isAuthenticated } = useAuth();
  const [page, setPage] = useState<number>(1);
  const [currentpage, setCurrentPage] = useState<number>();
  const [customers, setCustomers] = useState<any[]>([]);
  const [sortedData, setSortedData] = useState<any[]>([]);
  const [customerLoadder, setCustomerLoader] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCustomer, setTotalCustomer] = useState<number>(1);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | "">("");
  const [sortOrderKyc, setSortOrderKyc] = useState<"asc" | "desc" | "">("");
  const [sortOrderDate, setSortOrderDate] = useState<"asc" | "desc" | "">("");
  const [status, setStatus] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [viewMode, setViewMode] = useState<"all" | 2 | 3>("all");
  const [statusDropdownOpen, setStatusDropdownOpen] = useState<boolean>(false);

  // Debounce search input — reset page to 1 on new query
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (!isAuthenticated) return;

    setCustomerLoader(true);

    const fetchData = async () => {
      try {
        const kycParam = viewMode !== "all" ? `&kyc=${viewMode}` : "";
        const searchParam = debouncedSearch
          ? `&search=${encodeURIComponent(debouncedSearch)}`
          : "";
        const { data } = await axiosInstance.get(
          `${API_URL}api/users/me/kyc/logs?limit=25&page=${page}${kycParam}${searchParam}`
        );

        const list = data?.data?.data ?? [];
        const meta = data?.data?.meta ?? {};
        const sorted = [...list].sort(
          (a: any, b: any) =>
            new Date(getLogDate(b, viewMode)).getTime() -
            new Date(getLogDate(a, viewMode)).getTime()
        );

        setTotalPages(meta.totalPages ?? 1);
        setCurrentPage(meta.currentPage ?? 1);
        setTotalCustomer(meta.totalRecords ?? 0);
        setCustomers(sorted);
      } catch (error: any) {
        console.log(error.response?.data?.message || "An error occurred");
      } finally {
        setCustomerLoader(false);
      }
    };

    fetchData();
  }, [page, isAuthenticated, refetch, viewMode, debouncedSearch]);

  const filteredCustomers = React.useMemo(() => {
    let list =
      viewMode === "all"
        ? [...customers]
        : viewMode === 2
        ? customers.filter(belongsToLevel2)
        : customers.filter(belongsToLevel3);
    if (status !== "all") {
      const statusMap: Record<string, string> = {
        Approved: "completed/approved",
        Completed: "completed/approved",
        Rejected: "rejected",
        Pending: "pending",
        Requests: "pending",
      };
      const target = statusMap[status] ?? status.toLowerCase();
      list = list.filter((log) => {
        const s = getRowStatus(log, viewMode);
        if (target === "rejected") return s === "rejected";
        return s === target;
      });
    }
    return list;
  }, [customers, viewMode, status]);

  const statusOrder = (s: string) => {
    if (s === "not-started") return 0;
    if (s === "pending") return 1;
    if (s === "completed/approved") return 2;
    if (s === "rejected") return 3;
    return 4;
  };

  useEffect(() => {
    let next = [...filteredCustomers];
    if (sortOrder === "asc") {
      next.sort((a, b) => getLogName(a).localeCompare(getLogName(b)));
    } else if (sortOrder === "desc") {
      next.sort((a, b) => getLogName(b).localeCompare(getLogName(a)));
    } else if (sortOrderDate === "asc") {
      next.sort(
        (a, b) =>
          new Date(getLogDate(a, viewMode)).getTime() -
          new Date(getLogDate(b, viewMode)).getTime()
      );
    } else if (sortOrderDate === "desc") {
      next.sort(
        (a, b) =>
          new Date(getLogDate(b, viewMode)).getTime() -
          new Date(getLogDate(a, viewMode)).getTime()
      );
    } else if (sortOrderKyc === "asc") {
      next.sort(
        (a, b) =>
          statusOrder(getRowStatus(a, viewMode)) -
          statusOrder(getRowStatus(b, viewMode))
      );
    } else if (sortOrderKyc === "desc") {
      next.sort(
        (a, b) =>
          statusOrder(getRowStatus(b, viewMode)) -
          statusOrder(getRowStatus(a, viewMode))
      );
    }
    setSortedData(next);
  }, [sortOrder, sortOrderDate, sortOrderKyc, viewMode, filteredCustomers]);

  // console.log(merchants);

  const statusOptions = [
    { value: "all", label: "All statuses" },
    { value: "Approved", label: "Completed" },
    { value: "Rejected", label: "Rejected" },
    { value: "Pending", label: "Requests" },
  ];
  const statusLabel =
    statusOptions.find((o) => o.value === status)?.label ?? "All statuses";

  return (
    <div className="w-full h-full">
      <div className="w-full p-4 bg-[#0D0D0D] border-b border-[#1C2227]">
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-wrap items-center gap-2">
            {(["all", 2, 3] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => {
                  setViewMode(mode);
                  setPage(1);
                }}
                className={
                  viewMode === mode
                    ? "px-4 py-2 rounded-lg font-semibold text-[14px] bg-[#0C84DA] text-white border border-[#0C84DA]"
                    : "px-4 py-2 rounded-lg font-medium text-[14px] text-[#869AA9] bg-[#141414] border border-[#1C2227] hover:border-[#2a3540] hover:text-gray-200"
                }
              >
                {mode === "all" ? "All logs" : `Level ${mode}`}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 w-full px-3 py-2.5 rounded-xl bg-[#191919] border border-[#1C2227] flex items-center gap-2 focus-within:border-[#0C84DA]/50 transition-colors">
              <CiSearch className="text-[20px] text-[#617889] flex-shrink-0" />
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 bg-transparent outline-none text-[14px] text-gray-200 placeholder:text-[#617889]"
                placeholder="Search by name"
              />
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setStatusDropdownOpen((o) => !o)}
                className="px-4 py-2.5 rounded-xl bg-[#191919] border border-[#1C2227] flex items-center gap-2 text-[14px] text-gray-200 hover:border-[#2a3540] min-w-[160px] justify-between"
              >
                <span>{statusLabel}</span>
                <svg
                  className={`w-4 h-4 text-[#617889] transition-transform ${
                    statusDropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {statusDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    aria-hidden="true"
                    onClick={() => setStatusDropdownOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-20 py-1 w-[180px] rounded-xl bg-[#1C2227] border border-[#2a3540] shadow-xl">
                    {statusOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setStatus(opt.value);
                          setStatusDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left text-[14px] hover:bg-[#252d35] transition-colors first:rounded-t-lg last:rounded-b-lg ${
                          status === opt.value
                            ? "text-[#0C84DA] font-medium bg-[#0C84DA]/10"
                            : "text-gray-200"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {customerLoadder ? (
        <div className="lgss:flex hidden gap-5 w-full  h-[480px]">
          <div className="relative w-full space-y-3 overflow-hidden  bg-[#5c6b73] bg-opacity-30 p-8 shadow before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-transparent before:via-white/40 hover:shadow-lg before:animate-[shimmer_1.3s_infinite]">
            <div className="h-36 w-full  bg-[#E7EBEE]"></div>

            <div className="grid grid-cols-6 gap-2 mt-4">
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
            </div>
            <div className="grid grid-cols-6 gap-2">
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
            </div>
            <div className="grid grid-cols-6 gap-2">
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
            </div>
            <div className="grid grid-cols-6 gap-2">
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
            </div>
            <div className="grid grid-cols-6 gap-2">
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
            </div>
            <div className="grid grid-cols-6 gap-2">
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
              <div className="h-8 w-full rounded-full bg-[#E7EBEE]"></div>
            </div>
          </div>
        </div>
      ) : customers.length >= 1 ? (
        <div>
          {sortedData.length === 0 ? (
            <div className="w-full flex flex-col justify-start h-full mt-16 mb-20 items-center">
              <img src={noTransaction} alt="" />
              <h4 className="font-bold text-[#869AA9] pt-2">
                No {status !== "all" ? status.toLowerCase() : ""} logs
                {viewMode !== "all" ? ` for Level ${viewMode}` : ""}
              </h4>
              <p className="text-[#869AA9] pt-2 text-[12px]">
                Try changing level or status filter
              </p>
            </div>
          ) : (
            <div className="bg-[#0D0D0D] overflow-auto scrollbar-thumb-[#60B7F6] scrollbar-track-white   scrollbar-thin   w-full h-[652px]  ">
              <table className="w-full">
                <thead>
                  <tr className="h-[56px] bg-[#1C2227] flex justify-start px-8  items-center ">
                    <th
                      onClick={() => {
                        if (sortOrder === "desc") {
                          setSortOrder("asc");
                        } else {
                          setSortOrder("desc");
                        }
                      }}
                      className="text-[#617889] gap-12 cursor-pointer mr-4 flex justify-start items-center text-[15px] w-1/4  whitespace-nowrap "
                    >
                      Name
                      <div className="flex flex-col justify-center items-center">
                        <TiArrowSortedUp
                          className={
                            sortOrder === "asc"
                              ? "text-[#BDBDBE] text-[14px]"
                              : "text-[#617889] text-[12px]"
                          }
                        />
                        <TiArrowSortedDown
                          className={
                            sortOrder === "desc"
                              ? "text-[#BDBDBE] text-[14px]"
                              : "text-[#617889] text-[12px]"
                          }
                        />
                      </div>
                    </th>
                    <th className="text-[#617889] text-[15px] w-1/4 mr-4 whitespace-nowrap text-center overflow-hidden">
                      Level
                    </th>
                    <th
                      onClick={() => {
                        if (sortOrderKyc === "desc") {
                          setSortOrderKyc("asc");
                        } else {
                          setSortOrderKyc("desc");
                        }
                      }}
                      className="text-[#617889] gap-6 cursor-pointer  flex justify-center items-center text-[15px] w-1/4  whitespace-nowrap "
                    >
                      KYC Status
                      <div className="flex flex-col justify-center items-center">
                        <TiArrowSortedUp
                          className={
                            sortOrderKyc === "asc"
                              ? "text-[#BDBDBE] text-[14px]"
                              : "text-[#617889] text-[12px]"
                          }
                        />
                        <TiArrowSortedDown
                          className={
                            sortOrderKyc === "desc"
                              ? "text-[#BDBDBE] text-[14px]"
                              : "text-[#617889] text-[12px]"
                          }
                        />
                      </div>
                    </th>

                    <th
                      onClick={() => {
                        if (sortOrderDate === "desc") {
                          setSortOrderDate("asc");
                        } else {
                          setSortOrderDate("desc");
                        }
                      }}
                      className="text-[#617889] gap-4 cursor-pointer flex mr-4 justify-start items-center text-[15px] w-1/4  whitespace-nowrap "
                    >
                      Date Created
                      <div className="flex flex-col justify-center items-center">
                        <TiArrowSortedUp
                          className={
                            sortOrderDate === "desc"
                              ? "text-[#BDBDBE] text-[12px]"
                              : "text-[#617889] text-[12px]"
                          }
                        />
                        <TiArrowSortedDown
                          className={
                            sortOrderDate === "asc"
                              ? "text-[#BDBDBE] text-[12px]"
                              : "text-[#617889] text-[12px]"
                          }
                        />
                      </div>
                    </th>
                  </tr>
                </thead>
                {sortedData.map((customer, index) => {
                  const rowStatus = getRowStatus(customer, viewMode);
                  const statusLabel =
                    rowStatus === "completed/approved"
                      ? "Completed"
                      : rowStatus === "not-started"
                      ? "Not started"
                      : rowStatus === "rejected"
                      ? "Rejected"
                      : "Pending";
                  const dateString = getLogDate(customer, viewMode);
                  const date = dateString ? new Date(dateString) : null;
                  const day = date?.getDate() ?? 0;
                  const ordinalSuffix = (d: number) => {
                    if ([1, 21, 31].includes(d)) return "st";
                    if ([2, 22].includes(d)) return "nd";
                    if ([3, 23].includes(d)) return "rd";
                    return "th";
                  };
                  const formattedDate = date
                    ? `${date.toLocaleString("default", {
                        month: "short",
                      })} ${day}${ordinalSuffix(
                        day
                      )}, ${date.getFullYear()}, ${date.toLocaleString(
                        "default",
                        {
                          hour: "numeric",
                          minute: "numeric",
                          hour12: true,
                        }
                      )}`
                    : "—";

                  return (
                    <tbody
                      onClick={() => {
                        setCustomerDeet(customer);
                        setTimeout(() => setOpenCustomerLog(true), 400);
                      }}
                      key={customer._id ?? index}
                      className="cursor-pointer"
                    >
                      <tr className="h-[56px] w-full flex justify-start px-8 items-center">
                        <td className="text-white text-[15px] w-1/4 whitespace-nowrap capitalize mr-4 text-left overflow-hidden">
                          {getLogName(customer)}
                        </td>
                        <td className="text-white text-[15px] w-1/4 whitespace-nowrap mr-6 text-center overflow-hidden">
                          Level{" "}
                          {viewMode === "all"
                            ? getEffectiveLevel(customer)
                            : viewMode}
                        </td>
                        <td className="flex items-center justify-center w-1/5 text-[12px] font-bold">
                          <span
                            className={
                              rowStatus === "pending"
                                ? "text-[#CB9200] flex justify-center items-center w-2/3 bg-[#181818] tracking-wider h-[26px] rounded-full text-[9px] font-bold"
                                : rowStatus === "rejected"
                                ? "text-red-500 flex justify-center items-center w-2/3 bg-[#181818] tracking-wider h-[26px] rounded-full text-[9px] font-bold"
                                : rowStatus === "completed/approved"
                                ? "text-[#03C668] flex justify-center items-center w-2/3 bg-[#0E2B1F] tracking-wider h-[26px] rounded-full text-[9px] font-bold"
                                : "text-[#869AA9] flex justify-center items-center w-2/3 bg-[#181818] tracking-wider h-[26px] rounded-full text-[9px] font-bold"
                            }
                          >
                            {statusLabel}
                          </span>
                        </td>
                        <td className="text-white text-[15px] w-1/4 whitespace-nowrap mr-4 text-left overflow-hidden">
                          {formattedDate}
                        </td>
                      </tr>
                      <tr className="w-full">
                        <td className="mt-1 w-full h-[1px] bg-[#13181B]" />
                      </tr>
                    </tbody>
                  );
                })}
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="w-full flex flex-col justify-start h-full mt-16 mb-20 items-center">
          <img src={noTransaction} alt="" />
          <h4 className="font-bold text-[#869AA9] pt-2">
            No customer logs yet
          </h4>
          <p className="text-[#869AA9] pt-2 text-[12px]">
            KYC logs will show up here
          </p>
        </div>
      )}
      {customers.length >= 1 && (
        <div className="flex gap-4 mt-14 justify-end items-center">
          <h4 className="font-bold font-sora text-[14px] text-white">
            Page {currentpage} of {totalPages}
            {totalCustomer != null && ` (${totalCustomer} records)`}
          </h4>
          <button className="flex justify-center items-center">
            <BsArrowLeftSquare
              onClick={() => {
                if (page > 1) {
                  setPage((prev) => prev - 1);
                }
              }}
              className="text-3xl cursor-pointer text-[#C6D0D7] font-bold"
            />
          </button>

          <button>
            <BsArrowRightSquare
              onClick={() => {
                if (page < totalPages) {
                  setPage((prev) => prev + 1);
                }
              }}
              className="text-3xl cursor-pointer text-[#C6D0D7]"
            />
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomerLogs;
